import { useState, useRef, useEffect } from 'react';
import { useInvalidateRelease } from '@/lib/invalidateRelease';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@/utils/toast';
import { PostVariation } from '@/types/variations';
import { getVariationStatusCounts } from '@/utils/calendar';
import { variationsService } from '@/services/variations.service';
import { OCCURRENCE_STATUS_BADGES } from '@/utils/calendarStatus';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { ICONS } from '@/constants/icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { ConfirmDialogSimple } from '@/components/ui/ConfirmDialog';
import { PromptDialog } from '@/components/ui/PromptDialog';
import { RetryImage } from '@/components/ui';
import { Checkbox } from '@/components/ui/Checkbox';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';
import { formatApiError } from '@/services/api';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { openReleaseDrawer } from '@/stores/releaseDrawer.store';
import { ROW_ACTION_MENU_DIVIDER, ROW_ACTION_MENU_ITEM, ROW_ACTION_MENU_SURFACE } from '@/components/ui/rowActionMenuStyles';

/**
 * VariationsTable Component
 *
 * Table displaying all variations with PostTable-inspired design:
 * - Media (variation + original side-by-side)
 * - Variation title + caption (with show more/less)
 * - Original post reference
 * - Status badges (calendar mappings)
 * - Actions (Edit, Duplicate, Delete)
 */

/**
 * True when the release workflow is active for the brand AND this variation has a release record.
 * In that case the Workflow state is the source of truth and the Draft/Ready content chip is hidden.
 */
const hasReleaseRecord = (variation: PostVariation, workflowEnabled: boolean): boolean =>
  workflowEnabled && !!variation.workflowStatus && variation.workflowStatus !== 'UNSCHEDULED';

const WorkflowStateChip = ({ status }: { status: PostVariation['workflowStatus'] }) => {
  const { t } = useTranslation('common');
  if (status === 'PENDING') {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs whitespace-nowrap bg-yellow-100 text-yellow-700">
        <Icon name={ICONS.REVIEW} size="sm" ariaHidden />
        <span>{t('workflow_pending_review')}</span>
      </div>
    );
  }
  if (status === 'APPROVED') {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs whitespace-nowrap bg-green-100 text-green-700">
        <Icon name={ICONS.CHECK} size="sm" ariaHidden />
        <span>{t('workflow_released')}</span>
      </div>
    );
  }
  if (status === 'CHANGES_REQUESTED') {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs whitespace-nowrap bg-red-100 text-red-700">
        <Icon name={ICONS.CAUTION} size="sm" ariaHidden />
        <span>{t('workflow_changes_requested')}</span>
      </div>
    );
  }
  return null;
};

interface VariationsTableProps {
  variations: PostVariation[];
  isLoading: boolean;
  error: Error | null;
  onRefresh: () => void;
  onEdit?: (variation: PostVariation) => void;
  onEditPost?: (postId: number) => void;
  getVariationType: (variation: PostVariation) => 'Language' | 'Custom';
  selectedVariations: number[];
  onSelect: (variationId: number) => void;
  onSelectAll: () => void;
  onSendForReview?: (variation: PostVariation) => void;
  workflowEnabled?: boolean;
  /** When provided, hides the Edit action for variations where this returns false. */
  canEditVariation?: (variation: PostVariation) => boolean;
  /** When provided, hides the Delete action for variations where this returns false. */
  canDeleteVariation?: (variation: PostVariation) => boolean;
}

export const VariationsTable = ({
  variations,
  isLoading,
  error,
  onRefresh,
  onEdit,
  onEditPost,
  getVariationType,
  selectedVariations,
  onSelect,
  onSelectAll,
  onSendForReview,
  workflowEnabled = false,
  canEditVariation,
  canDeleteVariation,
}: VariationsTableProps) => {
  const { t } = useTranslation('common');
  const { formatRelativeDate } = useTimezone();
  const { canCreateVariation } = useReviewerRestrictions();
  const invalidateRelease = useInvalidateRelease();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // State for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    postId: number | null;
    variationId: number | null;
    title: string;
  }>({
    isOpen: false,
    postId: null,
    variationId: null,
    title: '',
  });

  // State for duplicate prompt
  const [duplicateTarget, setDuplicateTarget] = useState<PostVariation | null>(null);

  // State for 3-dot dropdown menu
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  /** The actions button that opened the menu: focus returns to it on Escape. */
  const menuTriggerRef = useRef<HTMLElement | null>(null);

  // Escape closes the open row actions menu (card menu and portaled table menu) and returns focus to its trigger.
  useEffect(() => {
    if (openMenuId === null) return;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key !== 'Escape' || ev.defaultPrevented) return;
      setOpenMenuId(null);
      setMenuPosition(null);
      menuTriggerRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openMenuId]);

  // State for caption expansion
  const [expandedCaptions, setExpandedCaptions] = useState<Set<number>>(new Set());
  const [overflowingCaptions, setOverflowingCaptions] = useState<Set<number>>(new Set());
  const captionRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // State for log drawer

  /**
   * Select all logic
   */
  const allSelected = variations.length > 0 && selectedVariations.length === variations.length;

  /**
   * Toggle caption expansion for a variation
   */
  const toggleCaptionExpansion = (variationId: number) => {
    setExpandedCaptions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(variationId)) {
        newSet.delete(variationId);
      } else {
        newSet.add(variationId);
      }
      return newSet;
    });
  };

  /**
   * Check for caption overflow to determine when to show "show more" button
   */
  useEffect(() => {
    const checkOverflow = () => {
      setOverflowingCaptions((prev) => {
        const newOverflowing = new Set<number>(prev);

        captionRefs.current.forEach((el, variationId) => {
          // Only check captions that are NOT expanded
          if (!expandedCaptions.has(variationId)) {
            if (el && el.scrollHeight > el.clientHeight) {
              newOverflowing.add(variationId);
            } else {
              newOverflowing.delete(variationId);
            }
          }
        });

        return newOverflowing;
      });
    };

    const timeoutId = setTimeout(checkOverflow, 50);

    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variations]);

  /**
   * Calculate word count from text
   */
  const getWordCount = (text: string): number => {
    return text.trim() ? text.trim().split(/\s+/).filter(Boolean).length : 0;
  };

  /**
   * Check if variation has custom (different) media compared to original post
   */
  const hasCustomMedia = (variation: PostVariation): boolean => {
    const varMedia = variation.postMedia || [];
    // No media on variation = inherits from original post, not custom
    if (varMedia.length === 0) return false;
    const postMediaIds = (variation.post?.postMedia || []).map((m) => m.id).sort();
    const varMediaIds = varMedia.map((m) => m.id).sort();
    if (varMediaIds.length !== postMediaIds.length) return true;
    return varMediaIds.some((id, i) => id !== postMediaIds[i]);
  };

  /**
   * Aggregate calendar mapping + instancesSummary statuses for a variation.
   *
   * Unions `variation.calendarMappings` with `variation.instancesSummary`
   * (mirroring the post-level `instancesSummary` handling used by the
   * sidebar/grid variation badges) so a PARTIAL variation (some channels
   * published, some failed) surfaces a Failed badge alongside Published,
   * instead of only showing Published as it did when this only tallied raw
   * `calendarMappings[].status`. When `instancesSummary` is absent, this
   * degrades to the raw mapping counts only (unchanged legacy behavior).
   *
   * Stage 2c-ii: `total` now also counts the first-class partial/missed
   * buckets (un-folded from `failed` in `instancesSummary` — see
   * POST-STATUS-MODEL.md) so a variation with ONLY a partial/missed
   * occurrence doesn't falsely fall through to the "unmapped" state below.
   *
   * Task #182 (Low/Medium): `total` previously summed only 6 of the 8
   * first-class buckets — omitting `cancelled` and `publishing` — so a
   * variation whose ONLY occurrence was CANCELLED or transiently PUBLISHING
   * computed `total === 0` and wrongly rendered the "unmapped" (☁️) fallback
   * below, even though it WAS mapped/scheduled. FIX: prefer the backend's
   * authoritative `instancesSummary.total` (mirrors how `PostTable.tsx` uses
   * `post.instancesSummary.total` rather than hand-summing a subset of
   * buckets — see `computeInstancesSummary` in `publish-state.ts`, which
   * counts ALL 8 buckets). Only fall back to summing when `instancesSummary`
   * is absent, and even then include `cancelled`/`publishing` so the
   * fallback total can never under-count either.
   */
  const getMappingStatusCounts = (variation: PostVariation) => {
    const counts = getVariationStatusCounts(variation);
    const authoritativeTotal = variation.instancesSummary?.total;
    return {
      ...counts,
      total:
        authoritativeTotal !== undefined
          ? authoritativeTotal
          : counts.mapped +
            counts.scheduled +
            counts.published +
            counts.failed +
            (counts.partial ?? 0) +
            (counts.missed ?? 0) +
            (counts.cancelled ?? 0) +
            (counts.publishing ?? 0),
    };
  };

  /**
   * Truncate text helper
   */
  const truncate = (text: string | undefined, maxLength: number): string => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  /**
   * Delete mutation
   */
  const deleteMutation = useMutation({
    mutationFn: ({ postId, variationId }: { postId: number; variationId: number }) =>
      variationsService.delete(postId, variationId),
    onSuccess: () => {
      toast.success(t('common:variation_deleted') || 'Variation deleted');
      invalidateRelease();
      onRefresh();
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });

  /**
   * Duplicate mutation
   */
  const duplicateMutation = useMutation({
    mutationFn: ({
      postId,
      variationId,
      newTitle,
    }: {
      postId: number;
      variationId: number;
      newTitle: string;
    }) => variationsService.duplicate(postId, variationId, newTitle),
    onSuccess: () => {
      toast.success(t('common:variation_duplicated') || 'Variation duplicated');
      invalidateRelease();
      onRefresh();
    },
    onError: (error) => {
      toast.error(formatApiError(error).message);
    },
  });

  /**
   * Handle edit
   */
  const handleEdit = (variation: PostVariation) => {
    setOpenMenuId(null);
    setMenuPosition(null);
    if (onEdit) {
      onEdit(variation);
    } else {
      toast.error('Edit functionality requires team-wide variations endpoint (coming soon)');
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = (variation: PostVariation) => {
    setOpenMenuId(null);
    setMenuPosition(null);
    setDeleteConfirm({
      isOpen: true,
      postId: variation.postId,
      variationId: variation.id,
      title: variation.title,
    });
  };

  const confirmDelete = () => {
    if (deleteConfirm.postId && deleteConfirm.variationId) {
      deleteMutation.mutate({
        postId: deleteConfirm.postId,
        variationId: deleteConfirm.variationId,
      });
    }
    setDeleteConfirm({ isOpen: false, postId: null, variationId: null, title: '' });
  };

  /**
   * Handle duplicate
   */
  const handleDuplicate = (variation: PostVariation) => {
    setOpenMenuId(null);
    setMenuPosition(null);
    setDuplicateTarget(variation);
  };

  const confirmDuplicate = (newTitle: string) => {
    if (duplicateTarget && newTitle.trim()) {
      duplicateMutation.mutate({
        postId: duplicateTarget.postId,
        variationId: duplicateTarget.id,
        newTitle: newTitle.trim(),
      });
      setDuplicateTarget(null);
    }
  };

  /**
   * Loading skeleton
   */
  if (isLoading) {
    if (isMobile) {
      return (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 bg-gray-200 rounded" />
                  <div className="h-5 w-20 bg-gray-200 rounded" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-16 bg-gray-200 rounded" />
                  <div className="h-7 w-7 bg-gray-200 rounded" />
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-1/3 aspect-square bg-gray-200 rounded-lg flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-4/5" />
                  <div className="flex gap-1.5 mt-2">
                    <div className="h-5 w-14 bg-gray-200 rounded" />
                    <div className="h-5 w-12 bg-gray-200 rounded" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <table className="w-full">
            <thead className={cn('sticky z-10 bg-gray-50 border-b border-gray-200', selectedVariations.length > 0 ? 'top-[calc(4.1rem+59px+58px)]' : 'top-[calc(4.1rem+59px)]')}>
              <tr>
                <th className="w-10 p-2 md:p-3">
                </th>
                <th className="w-10 p-2 md:p-3">
                  <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                </th>
                <th className="hidden md:table-cell w-20 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                  {t('media')}
                </th>
                <th className="p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                  {t('variation')}
                </th>
                <th className="hidden md:table-cell max-w-xs p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                  {t('original_post')}
                </th>
                <th className="hidden md:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                  {t('status')}
                </th>
                {workflowEnabled && (
                  <th className="hidden md:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                    {t('workflow')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3].map((i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="p-2 md:p-3 align-top text-center">
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse mx-auto" />
                  </td>
                  <td className="p-2 md:p-3 align-middle">
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                  </td>
                  <td className="p-2 md:p-3 align-top">
                    <div className="h-14 w-14 bg-gray-200 rounded-lg animate-pulse" />
                  </td>
                  <td className="p-2 md:p-3 align-top">
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse" />
                      <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                      <div className="flex gap-2">
                        <div className="h-5 bg-gray-200 rounded w-16 animate-pulse" />
                        <div className="h-5 bg-gray-200 rounded w-14 animate-pulse" />
                      </div>
                    </div>
                  </td>
                  <td className="hidden md:table-cell p-2 md:p-3 align-top">
                    <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
                  </td>
                  <td className="hidden md:table-cell p-2 md:p-3 align-top">
                    <div className="h-6 bg-gray-200 rounded w-16 animate-pulse" />
                  </td>
                  {workflowEnabled && (
                    <td className="hidden md:table-cell p-2 md:p-3 align-top">
                      <div className="h-6 bg-gray-200 rounded w-20 animate-pulse" />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6">
        <div className="flex items-center gap-3 text-red-600">
          <Icon name={ICONS.CAUTION} size="lg" />
          <div>
            <p className="font-medium">{t('common:error_loading') || 'Error loading variations'}</p>
            <p className="text-sm text-red-500 mt-1">{error.message}</p>
          </div>
        </div>
        <Button variant="secondary" onClick={onRefresh} className="mt-4">
          <Icon name={ICONS.REFRESH} size="sm" className="mr-2" />
          {t('common:retry') || 'Retry'}
        </Button>
      </div>
    );
  }

  /**
   * Empty state
   */
  if (variations.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
        <Icon name={ICONS.COPY} size="xl" className="text-gray-300 mb-4 mx-auto" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          {t('common:no_variations') || 'No variations found'}
        </h3>
        <p className="text-gray-500 mb-4">
          {t('common:no_variations_description') || 'Create variations from your posts in the Creator page'}
        </p>
      </div>
    );
  }

  /**
   * Mobile card view
   */
  if (isMobile) {
    return (
      <>
        <div className="space-y-3">
          {variations.map((variation) => {
            const varType = getVariationType(variation);
            const customMedia = hasCustomMedia(variation);
            const firstVariationMedia = variation.postMedia && variation.postMedia.length > 0 ? variation.postMedia[0] : null;
            const firstOriginalMedia = variation.post?.postMedia && variation.post.postMedia.length > 0 ? variation.post.postMedia[0] : null;
            const statusCounts = getMappingStatusCounts(variation);
            const effectiveCaption = variation.caption || variation.post?.caption || '';
            const charCount = effectiveCaption.length;
            const wordCount = getWordCount(effectiveCaption);
            const captionDiffers = !!variation.caption && variation.caption !== variation.post?.caption;
            const selected = selectedVariations.includes(variation.id);

            return (
              <div
                key={variation.id}
                className={cn(
                  'group/card bg-white rounded-xl border p-4 space-y-3 transition-colors',
                  selected ? 'border-light-teal-gray bg-light-teal-gray/10' : 'border-gray-100 shadow-sm'
                )}
              >
                {/* Row 1: Checkbox + status badge | actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => onSelect(variation.id)}
                      aria-label={t('select_variation', { id: variation.id })}
                    />
                    {/* eslint-disable i18next/no-literal-string */}
                    {!hasReleaseRecord(variation, workflowEnabled) && variation.contentStatus === 'DRAFT' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                        ✏️ {t('draft', { defaultValue: 'Draft' })}
                      </span>
                    )}
                    {!hasReleaseRecord(variation, workflowEnabled) && variation.contentStatus === 'READY' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                        🚀 {t('ready_to_shoot', { defaultValue: 'Ready to Shoot' })}
                      </span>
                    )}
                    {/* eslint-enable i18next/no-literal-string */}
                    {hasReleaseRecord(variation, workflowEnabled) && <WorkflowStateChip status={variation.workflowStatus} />}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Actions button */}
                    <div className="relative">
                      <Tooltip content={t('actions')}>
                        <button
                          type="button"
                          onClick={(e) => {
                            if (openMenuId === variation.id) {
                              setOpenMenuId(null);
                            } else {
                              menuTriggerRef.current = e.currentTarget;
                              setOpenMenuId(variation.id);
                            }
                          }}
                          className="w-7 h-7 flex items-center justify-center bg-soft-mint hover:bg-soft-mint/70 rounded-md transition-colors"
                          aria-label={t('variation_actions') || 'Actions'}
                          aria-haspopup="menu"
                          aria-expanded={openMenuId === variation.id}
                        >
                          <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" color="text-dark-teal" ariaHidden />
                        </button>
                      </Tooltip>

                      {openMenuId === variation.id && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setOpenMenuId(null)}
                          />
                          <div className={`absolute right-0 top-full z-50 mt-1 max-h-[60vh] overflow-y-auto ${ROW_ACTION_MENU_SURFACE}`}>
                            {(!canEditVariation || canEditVariation(variation)) && (
                              <button
                                onClick={() => handleEdit(variation)}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              >
                                <Icon name={ICONS.EDIT} size="sm" ariaHidden />
                                {t('edit')}
                              </button>
                            )}
                            {canCreateVariation && (
                            <button
                              onClick={() => handleDuplicate(variation)}
                              className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                              disabled={duplicateMutation.isPending}
                            >
                              <Icon name={ICONS.COPY} size="sm" ariaHidden />
                              {t('duplicate')}
                            </button>
                            )}
                            {workflowEnabled && onSendForReview && variation.workflowStatus !== 'PENDING' && variation.workflowStatus !== 'APPROVED' && (
                              <>
                                <div className={ROW_ACTION_MENU_DIVIDER} />
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    setMenuPosition(null);
                                    onSendForReview(variation);
                                  }}
                                  className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                                >
                                  <Icon name={ICONS.REVIEW} size="sm" ariaHidden />
                                  {t('send_for_review', { defaultValue: 'Send for Review' })}
                                </button>
                              </>
                            )}
                            {/* Conversation is available wherever the release workflow applies. */}
                            {workflowEnabled && (
                              <>
                                <div className={ROW_ACTION_MENU_DIVIDER} />
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    setMenuPosition(null);
                                    openReleaseDrawer({ postId: variation.postId, variationId: variation.id, tab: 'comments', source: 'list' });
                                  }}
                                  className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                                >
                                  <Icon name={ICONS.CHAT} size="sm" ariaHidden />
                                  {t('release_open_conversation')}
                                </button>
                              </>
                            )}
                            {(!canDeleteVariation || canDeleteVariation(variation)) && (
                              <>
                                <div className={ROW_ACTION_MENU_DIVIDER} />
                                <button
                                  onClick={() => handleDelete(variation)}
                                  className={ROW_ACTION_MENU_ITEM + ' text-red-600 hover:bg-red-50 focus-visible:bg-red-50'}
                                  disabled={deleteMutation.isPending}
                                >
                                  <Icon name={ICONS.DELETE} size="sm" ariaHidden />
                                  {t('delete')}
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Row 2: Thumbnail (1/3) + content (2/3) */}
                <div className="flex gap-3 items-start">
                  {/* Thumbnail */}
                  <div className="relative w-1/3 aspect-square rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                    {(() => {
                      const mediaToShow = customMedia ? firstVariationMedia : firstOriginalMedia;
                      if (mediaToShow) {
                        return (
                          <>
                            <RetryImage
                              src={mediaToShow.thumbnailUrl || mediaToShow.originalUrl || undefined}
                              alt=""
                              className={`w-full h-full ${
                                mediaToShow.type === 'VIDEO' || mediaToShow.type === 'PDF'
                                  ? 'object-contain bg-gray-900'
                                  : 'object-cover'
                              }`}
                            />
                            {(mediaToShow.type === 'VIDEO' || mediaToShow.type === 'PDF') && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1 py-0.5">
                                <span className="text-white text-[10px]">
                                  {mediaToShow.type === 'VIDEO' ? '▶' : '📄'}
                                </span>
                              </div>
                            )}
                          </>
                        );
                      }
                      return (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Icon name={ICONS.IMAGE} size="sm" ariaHidden />
                        </div>
                      );
                    })()}
                    {/* Custom media badge */}
                    {customMedia && (
                      <Tooltip content={t('custom_media_tooltip', { defaultValue: 'Media differs from original post' })} side="right">
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 bg-amber-400 text-white text-[9px] font-bold rounded-full border-2 border-white shadow-sm cursor-default">
                          ✎
                        </span>
                      </Tooltip>
                    )}
                  </div>

                  {/* Content: title + type badge + caption */}
                  <div className="flex-1 min-w-0 space-y-1">
                    {/* Title + type badge */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 text-sm">{variation.title}</span>
                      {varType === 'Language' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-gray-700 border border-gray-300">
                          {t('variation_language_label')}
                        </span>
                      )}
                    </div>

                    {/* Caption */}
                    {effectiveCaption ? (
                      <div
                        ref={(el) => {
                          if (el) captionRefs.current.set(variation.id, el);
                          else captionRefs.current.delete(variation.id);
                        }}
                        className="overflow-hidden transition-all duration-300 ease-in-out"
                        style={{
                          maxHeight: expandedCaptions.has(variation.id) ? '500px' : '3.6em',
                        }}
                      >
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {effectiveCaption}
                          {captionDiffers && (
                            <Tooltip content={t('custom_caption_tooltip', { defaultValue: 'Caption differs from original post' })} side="top">
                              {/* eslint-disable-next-line i18next/no-literal-string */}
                              <span className="ml-1 inline-flex items-center justify-center w-4 h-4 bg-amber-100 text-amber-600 text-[10px] rounded-full cursor-default">
                                ✎
                              </span>
                            </Tooltip>
                          )}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">{t('no_caption')}</p>
                    )}
                    {(overflowingCaptions.has(variation.id) || expandedCaptions.has(variation.id)) && (
                      <button
                        onClick={() => toggleCaptionExpansion(variation.id)}
                        className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                      >
                        {expandedCaptions.has(variation.id) ? t('show_less') : t('show_more')}
                        <span
                          className="inline-block transition-transform duration-300"
                          style={{
                            transform: expandedCaptions.has(variation.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        >
                          ↓
                        </span>
                      </button>
                    )}

                  </div>
                </div>

                {/* Row 3: meta badges — full width under thumb + caption */}
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                    {formatRelativeDate(variation.updatedAt)}
                  </span>
                  {charCount > 0 && (
                    <>
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                        {t('x_chars', { count: charCount })}
                      </span>
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                        {t('x_words', { count: wordCount })}
                      </span>
                    </>
                  )}
                  {statusCounts.mapped > 0 && (
                    <Tooltip content={t('mapped_to_calendar', { defaultValue: 'Mapped to Calendar' })}>
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded whitespace-nowrap">📅 {statusCounts.mapped}</span>
                    </Tooltip>
                  )}
                  {statusCounts.scheduled > 0 && (
                    <Tooltip content={t('scheduled', { defaultValue: 'Scheduled' })}>
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      <span className="px-1.5 py-0.5 bg-soft-mint text-dark-teal rounded whitespace-nowrap">🚀 {statusCounts.scheduled}</span>
                    </Tooltip>
                  )}
                  {/* First-class PUBLISHING bucket (Stage 2c-i / #182 B1) — was
                      previously counted in `total` but had no render branch,
                      leaving a blank cell for publishing-only variations. */}
                  {(statusCounts.publishing ?? 0) > 0 && (
                    <Tooltip content={t('status_publishing')}>
                      <span
                        className={`px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-0.5 ${OCCURRENCE_STATUS_BADGES.PUBLISHING.bgColor} ${OCCURRENCE_STATUS_BADGES.PUBLISHING.color}`}
                      >
                        <Icon name={OCCURRENCE_STATUS_BADGES.PUBLISHING.icon} size="xs" ariaHidden />
                        {statusCounts.publishing}
                      </span>
                    </Tooltip>
                  )}
                  {statusCounts.published > 0 && (
                    <Tooltip content={t('published', { defaultValue: 'Published' })}>
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded whitespace-nowrap">✅ {statusCounts.published}</span>
                    </Tooltip>
                  )}
                  {(statusCounts.partial ?? 0) > 0 && (
                    <Tooltip content={t('partial_success', { defaultValue: 'Partial success' })}>
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded whitespace-nowrap">🟡 {statusCounts.partial}</span>
                    </Tooltip>
                  )}
                  {statusCounts.failed > 0 && (
                    <Tooltip content={t('failed', { defaultValue: 'Failed' })}>
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded whitespace-nowrap">⚠️ {statusCounts.failed}</span>
                    </Tooltip>
                  )}
                  {(statusCounts.missed ?? 0) > 0 && (
                    <Tooltip content={t('missed', { defaultValue: 'Missed' })}>
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded whitespace-nowrap">⌛ {statusCounts.missed}</span>
                    </Tooltip>
                  )}
                  {/* First-class CANCELLED bucket (Stage 2c-i / #182 B1) — was
                      previously counted in `total` but had no render branch,
                      leaving a blank cell for cancelled-only variations. */}
                  {(statusCounts.cancelled ?? 0) > 0 && (
                    <Tooltip content={t('status_cancelled')}>
                      <span
                        className={`px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-0.5 ${OCCURRENCE_STATUS_BADGES.CANCELLED.bgColor} ${OCCURRENCE_STATUS_BADGES.CANCELLED.color}`}
                      >
                        <Icon name={OCCURRENCE_STATUS_BADGES.CANCELLED.icon} size="xs" ariaHidden />
                        {statusCounts.cancelled}
                      </span>
                    </Tooltip>
                  )}
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  {statusCounts.total === 0 && (
                    <Tooltip content={t('unmapped')}>
                      <span className="text-gray-400">☁️</span>
                    </Tooltip>
                  )}
                  {/* Edit button */}
                  {onEdit && (
                    <Tooltip content={t('edit')}>
                      <button
                        onClick={() => onEdit(variation)}
                        className="inline-flex items-center justify-center h-[18px] w-[18px] bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-colors"
                      >
                        <Icon name={ICONS.EDIT} size="xs" ariaHidden />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialogSimple
          open={deleteConfirm.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              setDeleteConfirm({ isOpen: false, postId: null, variationId: null, title: '' });
            }
          }}
          onConfirm={confirmDelete}
          title={t('common:confirm_delete') || 'Confirm Delete'}
          description={
            t('common:confirm_delete_variation_message', { title: deleteConfirm.title }) ||
            `Are you sure you want to delete the variation "${deleteConfirm.title}"? This action cannot be undone.`
          }
          confirmText={t('common:delete') || 'Delete'}
          variant="danger"
        />

        {/* Duplicate Variation Prompt */}
        <PromptDialog
          open={duplicateTarget !== null}
          onOpenChange={(open) => !open && setDuplicateTarget(null)}
          title={t('common:duplicate_variation_title', { defaultValue: 'Duplicate Variation' })}
          label={t('common:enter_variation_title', { defaultValue: 'Enter new variation title:' })}
          placeholder={t('common:variation_title_placeholder', { defaultValue: 'Variation title' })}
          defaultValue={duplicateTarget ? `${duplicateTarget.title} (Copy)` : ''}
          confirmText={t('common:duplicate', { defaultValue: 'Duplicate' })}
          required
          loading={duplicateMutation.isPending}
          onConfirm={confirmDuplicate}
        />

      </>
    );
  }

  /**
   * Table view
   */
  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <table className="w-full">
            <thead className={cn('sticky z-10 bg-gray-50 border-b border-gray-200', selectedVariations.length > 0 ? 'top-[calc(4.1rem+59px+58px)]' : 'top-[calc(4.1rem+59px)]')}>
              <tr>
                <th className="w-10 p-2 md:p-3">
                </th>
                <th className="w-10 p-2 md:p-3">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={onSelectAll}
                    aria-label={t('select_all_variations')}
                  />
                </th>
                <th className="hidden md:table-cell w-20 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                  {t('media')}
                </th>
                <th className="p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                  {t('variation')}
                </th>
                <th className="hidden md:table-cell max-w-xs p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                  {t('original_post')}
                </th>
                <th className="hidden md:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                  {t('status')}
                </th>
                {workflowEnabled && (
                  <th className="hidden md:table-cell w-32 p-2 md:p-3 text-left text-xs font-semibold text-gray-600">
                    {t('workflow')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {variations.map((variation) => {
                const varType = getVariationType(variation);
                const customMedia = hasCustomMedia(variation);
                const firstVariationMedia = variation.postMedia && variation.postMedia.length > 0 ? variation.postMedia[0] : null;
                const firstOriginalMedia = variation.post?.postMedia && variation.post.postMedia.length > 0 ? variation.post.postMedia[0] : null;
                const statusCounts = getMappingStatusCounts(variation);
                // Use variation caption if set, otherwise fall back to original post caption
                const effectiveCaption = variation.caption || variation.post?.caption || '';
                const charCount = effectiveCaption.length;
                const wordCount = getWordCount(effectiveCaption);
                // Only "differs" if variation has its own caption AND it's different from original
                const captionDiffers = !!variation.caption && variation.caption !== variation.post?.caption;

                const selected = selectedVariations.includes(variation.id);

                return (
                  <tr
                    key={variation.id}
                    className={`border-b border-gray-100 transition-colors group/row ${
                      selected ? 'bg-light-teal-gray/25' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Column 0: Actions - 3-dot menu */}
                    <td className="p-2 md:p-3 align-top text-center">
                      <div className="flex items-center justify-center h-14">
                        <Tooltip content={t('actions')}>
                          <button
                            type="button"
                            onClick={(e) => {
                              if (openMenuId === variation.id) {
                                setOpenMenuId(null);
                                setMenuPosition(null);
                              } else {
                                menuTriggerRef.current = e.currentTarget;
                                const rect = e.currentTarget.getBoundingClientRect();
                                const spaceBelow = window.innerHeight - rect.bottom;

                                // Horizontal positioning - open to the right
                                const left = rect.left;

                                // Vertical: if enough space below, use top; otherwise use bottom
                                if (spaceBelow >= 200) {
                                  setMenuPosition({ top: rect.bottom + 4, left });
                                } else {
                                  setMenuPosition({ bottom: window.innerHeight - rect.top + 4, left });
                                }
                                setOpenMenuId(variation.id);
                              }
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            aria-label={t('variation_actions') || 'Actions'}
                            aria-haspopup="menu"
                          aria-expanded={openMenuId === variation.id}
                          >
                            <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" color="text-gray-600" ariaHidden />
                          </button>
                        </Tooltip>

                        {/* Dropdown Menu */}
                        {openMenuId === variation.id && menuPosition && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => {
                                setOpenMenuId(null);
                                setMenuPosition(null);
                              }}
                            />
                            <div
                              className={`fixed z-50 max-h-[calc(100vh-2rem)] overflow-y-auto ${ROW_ACTION_MENU_SURFACE}`}
                              style={{
                                left: menuPosition.left,
                                ...(menuPosition.top !== undefined ? { top: menuPosition.top } : { bottom: menuPosition.bottom }),
                              }}
                            >
                              {(!canEditVariation || canEditVariation(variation)) && (
                                <button
                                  onClick={() => handleEdit(variation)}
                                  className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                                >
                                  <Icon name={ICONS.EDIT} size="sm" ariaHidden />
                                  {t('edit')}
                                </button>
                              )}
                              {canCreateVariation && (
                              <button
                                onClick={() => handleDuplicate(variation)}
                                className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                                disabled={duplicateMutation.isPending}
                              >
                                <Icon name={ICONS.COPY} size="sm" ariaHidden />
                                {t('duplicate')}
                              </button>
                              )}
                              {workflowEnabled && onSendForReview && variation.workflowStatus !== 'PENDING' && variation.workflowStatus !== 'APPROVED' && (
                                <>
                                  <div className={ROW_ACTION_MENU_DIVIDER} />
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setMenuPosition(null);
                                      onSendForReview(variation);
                                    }}
                                    className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                                  >
                                    <Icon name={ICONS.REVIEW} size="sm" ariaHidden />
                                    {t('send_for_review', { defaultValue: 'Send for Review' })}
                                  </button>
                                </>
                              )}
                              {/* Conversation is available wherever the release workflow applies. */}
                              {workflowEnabled && (
                                <>
                                  <div className={ROW_ACTION_MENU_DIVIDER} />
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setMenuPosition(null);
                                      openReleaseDrawer({ postId: variation.postId, variationId: variation.id, tab: 'comments', source: 'list' });
                                    }}
                                    className={ROW_ACTION_MENU_ITEM + ' text-gray-700 hover:bg-gray-50'}
                                  >
                                    <Icon name={ICONS.CHAT} size="sm" ariaHidden />
                                    {t('release_open_conversation')}
                                  </button>
                                </>
                              )}
                              {(!canDeleteVariation || canDeleteVariation(variation)) && (
                                <>
                                  <div className={ROW_ACTION_MENU_DIVIDER} />
                                  <button
                                    onClick={() => handleDelete(variation)}
                                    className={ROW_ACTION_MENU_ITEM + ' text-red-600 hover:bg-red-50 focus-visible:bg-red-50'}
                                    disabled={deleteMutation.isPending}
                                  >
                                    <Icon name={ICONS.DELETE} size="sm" ariaHidden />
                                    {t('delete')}
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>

                    {/* Column 1: Checkbox */}
                    <td className="p-2 md:p-3 align-top">
                      <div className="flex items-center h-14">
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => onSelect(variation.id)}
                          aria-label={t('select_variation', { id: variation.id })}
                        />
                      </div>
                    </td>

                    {/* Column 2: Media thumbnail */}
                    <td className="p-2 md:p-3 align-top">
                      {/* Outer wrapper: no overflow clip so badge can escape */}
                      <div className="relative w-14 h-14 flex-shrink-0">
                        {/* Image container: clips to rounded corners */}
                        <div className="w-full h-full bg-gray-100 rounded-lg overflow-hidden">
                          {(() => {
                            const mediaToShow = customMedia ? firstVariationMedia : firstOriginalMedia;
                            if (mediaToShow) {
                              return (
                                <>
                                  <RetryImage
                                    src={mediaToShow.thumbnailUrl || mediaToShow.originalUrl || undefined}
                                    alt=""
                                    className={`w-full h-full ${
                                      mediaToShow.type === 'VIDEO' || mediaToShow.type === 'PDF'
                                        ? 'object-contain bg-gray-900'
                                        : 'object-cover'
                                    }`}
                                  />
                                  {(mediaToShow.type === 'VIDEO' || mediaToShow.type === 'PDF') && (
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-1 py-0.5">
                                      <span className="text-white text-[10px]">
                                        {mediaToShow.type === 'VIDEO' ? '▶' : '📄'}
                                      </span>
                                    </div>
                                  )}
                                </>
                              );
                            }
                            return (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <Icon name={ICONS.IMAGE} size="sm" ariaHidden />
                              </div>
                            );
                          })()}
                        </div>
                        {/* Custom media badge - sits outside overflow-hidden */}
                        {customMedia && (
                          <Tooltip content={t('custom_media_tooltip', { defaultValue: 'Media differs from original post' })} side="right">
                            {/* eslint-disable-next-line i18next/no-literal-string */}
                            <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 bg-amber-400 text-white text-[9px] font-bold rounded-full border-2 border-white shadow-sm cursor-default">
                              ✎
                            </span>
                          </Tooltip>
                        )}
                      </div>
                    </td>

                    {/* Column 2: Variation title + caption */}
                    <td className="p-2 md:p-3 align-top">
                      <div className="space-y-1">
                        {/* Title with type badge */}
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{variation.title}</span>
                          {varType === 'Language' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-gray-700 border border-gray-300">
                              {t('variation_language_label')}
                            </span>
                          )}
                        </div>

                        {/* Caption with show more/less */}
                        {effectiveCaption ? (
                          <>
                            <div
                              ref={(el) => {
                                if (el) captionRefs.current.set(variation.id, el);
                                else captionRefs.current.delete(variation.id);
                              }}
                              className="overflow-hidden transition-all duration-300 ease-in-out"
                              style={{
                                maxHeight: expandedCaptions.has(variation.id) ? '500px' : '2.6em',
                              }}
                            >
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {effectiveCaption}
                                {captionDiffers && (
                                  <Tooltip content={t('custom_caption_tooltip', { defaultValue: 'Caption differs from original post' })} side="top">
                                    {/* eslint-disable-next-line i18next/no-literal-string */}
                                    <span className="ml-1 inline-flex items-center justify-center w-4 h-4 bg-amber-100 text-amber-600 text-[10px] rounded-full cursor-default">
                                      ✎
                                    </span>
                                  </Tooltip>
                                )}
                              </p>
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-gray-400 italic">{t('no_caption')}</p>
                        )}

                        {/* Badges row */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="whitespace-nowrap rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            {formatRelativeDate(variation.updatedAt)}
                          </span>
                          {charCount > 0 ? (
                            <>
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                                {t('x_chars', { count: charCount })}
                              </span>
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                                {t('x_words', { count: wordCount })}
                              </span>
                            </>
                          ) : null}

                          {/* Content status badge */}
                          {/* eslint-disable i18next/no-literal-string */}
                          {!hasReleaseRecord(variation, workflowEnabled) && variation.contentStatus === 'DRAFT' && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded flex items-center gap-1">
                              ✏️ {t('draft', { defaultValue: 'Draft' })}
                            </span>
                          )}
                          {/* eslint-enable i18next/no-literal-string */}
                          {!hasReleaseRecord(variation, workflowEnabled) && variation.contentStatus === 'READY' && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded flex items-center gap-1">
                              🚀 {t('ready_to_shoot', { defaultValue: 'Ready to Shoot' })}
                            </span>
                          )}

                          {/* Show more/less */}
                          {(overflowingCaptions.has(variation.id) || expandedCaptions.has(variation.id)) && (
                            <button
                              onClick={() => toggleCaptionExpansion(variation.id)}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 bg-soft-mint text-dark-teal text-xs font-medium rounded hover:bg-soft-mint/70 transition-all duration-200 ${
                                expandedCaptions.has(variation.id) ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100'
                              }`}
                            >
                              {expandedCaptions.has(variation.id) ? t('show_less') : t('show_more')}
                              <span
                                className="inline-block transition-transform duration-300"
                                style={{
                                  transform: expandedCaptions.has(variation.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                              >
                                ↓
                              </span>
                            </button>
                          )}
                          {/* Edit badge - visible on hover */}
                          {onEdit && (
                            <Tooltip content={t('edit')}>
                              <button
                                onClick={() => onEdit(variation)}
                                className="inline-flex items-center justify-center h-[20px] w-[20px] bg-primary-100 text-primary-700 rounded hover:bg-primary-200 transition-all duration-200 opacity-0 group-hover/row:opacity-100"
                              >
                                <Icon name={ICONS.EDIT} size="xs" ariaHidden />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Column 3: Original post (clickable to edit) */}
                    <td className="hidden md:table-cell p-2 md:p-3 align-top max-w-xs">
                      <Tooltip content={t('edit_original_post', { defaultValue: 'Edit original post' })} side="top">
                        <button
                          type="button"
                          onClick={() => variation.postId && onEditPost?.(variation.postId)}
                          className={cn(
                            'flex items-start gap-2 text-left w-full rounded-md p-1 -m-1 transition-colors',
                            onEditPost ? 'hover:bg-gray-100 cursor-pointer group/post' : 'cursor-default'
                          )}
                        >
                          {firstOriginalMedia && (
                            <div className="relative w-8 h-8 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                              <RetryImage
                                src={firstOriginalMedia.thumbnailUrl || firstOriginalMedia.originalUrl || undefined}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <span className={cn(
                            'text-sm text-gray-500 line-clamp-2',
                            onEditPost && 'group-hover/post:text-primary-600'
                          )}>
                            {truncate(variation.post?.caption, 80)}
                          </span>
                        </button>
                      </Tooltip>
                    </td>

                    {/* Column 4: Status (calendar mappings only) */}
                    <td className="hidden md:table-cell p-2 md:p-3 align-top">
                      <div className="flex items-center gap-1 text-xs flex-wrap">
                        {statusCounts.mapped > 0 && (
                          <Tooltip content={t('mapped_to_calendar', { defaultValue: 'Mapped to Calendar' })}>
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded whitespace-nowrap">
                              📅 {statusCounts.mapped}
                            </span>
                          </Tooltip>
                        )}
                        {statusCounts.scheduled > 0 && (
                          <Tooltip content={t('scheduled', { defaultValue: 'Scheduled' })}>
                            <span className="px-1.5 py-0.5 bg-soft-mint text-dark-teal rounded whitespace-nowrap">
                              🚀 {statusCounts.scheduled}
                            </span>
                          </Tooltip>
                        )}
                        {/* First-class PUBLISHING bucket (Stage 2c-i / #182 B1) — was
                            previously counted in `total` but had no render branch,
                            leaving a blank cell for publishing-only variations. */}
                        {(statusCounts.publishing ?? 0) > 0 && (
                          <Tooltip content={t('status_publishing')}>
                            <span
                              className={`px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-0.5 ${OCCURRENCE_STATUS_BADGES.PUBLISHING.bgColor} ${OCCURRENCE_STATUS_BADGES.PUBLISHING.color}`}
                            >
                              <Icon name={OCCURRENCE_STATUS_BADGES.PUBLISHING.icon} size="xs" ariaHidden />
                              {statusCounts.publishing}
                            </span>
                          </Tooltip>
                        )}
                        {statusCounts.published > 0 && (
                          <Tooltip content={t('published', { defaultValue: 'Published' })}>
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded whitespace-nowrap">
                              ✅ {statusCounts.published}
                            </span>
                          </Tooltip>
                        )}
                        {(statusCounts.partial ?? 0) > 0 && (
                          <Tooltip content={t('partial_success', { defaultValue: 'Partial success' })}>
                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded whitespace-nowrap">
                              🟡 {statusCounts.partial}
                            </span>
                          </Tooltip>
                        )}
                        {statusCounts.failed > 0 && (
                          <Tooltip content={t('failed', { defaultValue: 'Failed' })}>
                            {/* eslint-disable-next-line i18next/no-literal-string */}
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded whitespace-nowrap">
                              ⚠️ {statusCounts.failed}
                            </span>
                          </Tooltip>
                        )}
                        {(statusCounts.missed ?? 0) > 0 && (
                          <Tooltip content={t('missed', { defaultValue: 'Missed' })}>
                            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded whitespace-nowrap">
                              ⌛ {statusCounts.missed}
                            </span>
                          </Tooltip>
                        )}
                        {/* First-class CANCELLED bucket (Stage 2c-i / #182 B1) — was
                            previously counted in `total` but had no render branch,
                            leaving a blank cell for cancelled-only variations. */}
                        {(statusCounts.cancelled ?? 0) > 0 && (
                          <Tooltip content={t('status_cancelled')}>
                            <span
                              className={`px-1.5 py-0.5 rounded whitespace-nowrap inline-flex items-center gap-0.5 ${OCCURRENCE_STATUS_BADGES.CANCELLED.bgColor} ${OCCURRENCE_STATUS_BADGES.CANCELLED.color}`}
                            >
                              <Icon name={OCCURRENCE_STATUS_BADGES.CANCELLED.icon} size="xs" ariaHidden />
                              {statusCounts.cancelled}
                            </span>
                          </Tooltip>
                        )}
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        {statusCounts.total === 0 && <span className="text-gray-400">☁️ {t('unmapped')}</span>}
                      </div>
                    </td>

                    {/* Column 5: Workflow status */}
                    {workflowEnabled && (
                      <td className="hidden md:table-cell p-2 md:p-3 align-top">
                        <WorkflowStateChip status={variation.workflowStatus} />
                      </td>
                    )}

                  </tr>
                );
              })}
            </tbody>
          </table>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialogSimple
        open={deleteConfirm.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteConfirm({ isOpen: false, postId: null, variationId: null, title: '' });
          }
        }}
        onConfirm={confirmDelete}
        title={t('common:confirm_delete') || 'Confirm Delete'}
        description={
          t('common:confirm_delete_variation_message', { title: deleteConfirm.title }) ||
          `Are you sure you want to delete the variation "${deleteConfirm.title}"? This action cannot be undone.`
        }
        confirmText={t('common:delete') || 'Delete'}
        variant="danger"
      />

      {/* Duplicate Variation Prompt */}
      <PromptDialog
        open={duplicateTarget !== null}
        onOpenChange={(open) => !open && setDuplicateTarget(null)}
        title={t('common:duplicate_variation_title', { defaultValue: 'Duplicate Variation' })}
        label={t('common:enter_variation_title', { defaultValue: 'Enter new variation title:' })}
        placeholder={t('common:variation_title_placeholder', { defaultValue: 'Variation title' })}
        defaultValue={duplicateTarget ? `${duplicateTarget.title} (Copy)` : ''}
        confirmText={t('common:duplicate', { defaultValue: 'Duplicate' })}
        required
        loading={duplicateMutation.isPending}
        onConfirm={confirmDuplicate}
      />

    </>
  );
};
